import React, { useState, useEffect } from 'react';
import { 
  WiDaySunny, WiRain, WiCloudy, WiSnow, WiThunderstorm, WiFog, 
  WiHumidity, WiStrongWind, WiThermometer, WiSunrise, WiSunset,
  WiRaindrop, WiBarometer
} from 'weather-icons-react';
import { MapPin, Droplets, Wind, Thermometer, Eye, Gauge, Sun, Moon, AlertTriangle, CheckCircle, XCircle, RefreshCw } from 'lucide-react';
import { authAPI } from '../../../services/api';
import './dash.css';

const WeatherWidget = () => {
  const [weatherData, setWeatherData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userLocation, setUserLocation] = useState(null);
  const [selectedProduce, setSelectedProduce] = useState('');
  const [farmingInsight, setFarmingInsight] = useState(null);
  const [locationPermission, setLocationPermission] = useState(false);
  const [userProduce, setUserProduce] = useState([]);
  const [loadingProduce, setLoadingProduce] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  // Get user's current location and produce
  useEffect(() => {
    getCurrentLocation();
    fetchUserProduce();
  }, []);

  // Fetch user's produce from API
  const fetchUserProduce = async () => {
    try {
      setLoadingProduce(true);
      const response = await authAPI.getMyProduce();
      
      if (response.success && response.data) {
        setUserProduce(response.data);
      } else if (response.data && Array.isArray(response.data)) {
        // Handle case where response.data is directly an array
        setUserProduce(response.data);
      } else {
        // Fallback to common crops for testing
        setUserProduce([
          { id: 1, name: 'Yam' },
          { id: 2, name: 'Plantain' },
          { id: 3, name: 'Rice' },
          { id: 4, name: 'Maize' },
          { id: 5, name: 'Tomatoes' }
        ]);
      }
    } catch (error) {
      console.error('Error fetching user produce:', error);
      // Fallback to common crops for testing
      setUserProduce([
        { id: 1, name: 'Yam' },
        { id: 2, name: 'Plantain' },
        { id: 3, name: 'Rice' },
        { id: 4, name: 'Maize' },
        { id: 5, name: 'Tomatoes' }
      ]);
    } finally {
      setLoadingProduce(false);
    }
  };

  const getCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            lat: position.coords.latitude,
            lon: position.coords.longitude
          });
          setLocationPermission(true);
          fetchWeatherData(position.coords.latitude, position.coords.longitude);
        },
        (error) => {
          console.error('Location error:', error);
          setError('Location access denied. Using default location (Lagos).');
          setLocationPermission(false);
          // Fallback to Lagos coordinates
          fetchWeatherData(6.5244, 3.3792);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 300000 // 5 minutes
        }
      );
    } else {
      setError('Geolocation not supported. Using default location (Lagos).');
      setLocationPermission(false);
      fetchWeatherData(6.5244, 3.3792); // Lagos coordinates
    }
  };

  // Refresh weather data
  const refreshWeatherData = async () => {
    setRefreshing(true);
    setError(null);
    
    try {
      if (userLocation) {
        // Use current user location if available
        await fetchWeatherData(userLocation.lat, userLocation.lon);
      } else {
        // Try to get location again or use default
        getCurrentLocation();
      }
    } catch (error) {
      console.error('Error refreshing weather data:', error);
      setError('Failed to refresh weather data');
    } finally {
      setRefreshing(false);
    }
  };

  // Fetch weather data from multiple APIs for accuracy
  const fetchWeatherData = async (lat, lon) => {
    try {
      setLoading(true);
      setError(null);

      // Use OpenWeatherMap API (free tier allows 1000 calls/day)
      const API_KEY = 'd9427fe6ceeea54a3986bd4baf26350a'; // Replace with actual API key
      
      // Fetch both current weather and forecast
      const [currentResponse, forecastResponse] = await Promise.all([
        fetch(`https://api.openweathermap.org/data/2.5/weather?lat=${lat}&lon=${lon}&appid=${API_KEY}&units=metric`),
        fetch(`https://api.openweathermap.org/data/2.5/forecast?lat=${lat}&lon=${lon}&appid=${API_KEY}&units=metric`)
      ]);

      if (!currentResponse.ok || !forecastResponse.ok) {
        throw new Error('Weather API error');
      }

      const currentData = await currentResponse.json();
      const forecastData = await forecastResponse.json();
      
      
      // Process and enhance weather data
      const processedWeather = {
        location: await getLocationName(lat, lon),
        current: {
          temperature: Math.round(currentData.main.temp),
          feelsLike: Math.round(currentData.main.feels_like),
          condition: currentData.weather[0].main,
          description: currentData.weather[0].description,
          humidity: currentData.main.humidity,
          windSpeed: Math.round(currentData.wind.speed * 3.6), // Convert m/s to km/h
          windDirection: currentData.wind.deg || 0,
          pressure: currentData.main.pressure,
          visibility: Math.round((currentData.visibility || 10000) / 1000), // Convert m to km
          uvIndex: 0, // UV index not available in current weather API
          sunrise: new Date(currentData.sys.sunrise * 1000),
          sunset: new Date(currentData.sys.sunset * 1000),
          icon: currentData.weather[0].icon
        },
        forecast: processForecastData(forecastData.list),
        alerts: [] // Alerts not available in free tier
      };

      setWeatherData(processedWeather);
    } catch (error) {
      console.error('Weather fetch error:', error);
      // Fallback to mock data if API fails
      setWeatherData(generateMockWeatherData());
      setError('Using offline weather data');
    } finally {
      setLoading(false);
    }
  };

  // Process forecast data to get daily forecasts
  const processForecastData = (forecastList) => {
    // Group forecast items by date and get daily highs/lows
    const dailyData = {};
    
    forecastList.forEach(item => {
      const date = new Date(item.dt * 1000);
      const dateKey = date.toDateString();
      
      if (!dailyData[dateKey]) {
        dailyData[dateKey] = {
          date: date,
          temps: [item.main.temp],
          conditions: [item.weather[0].main],
          descriptions: [item.weather[0].description],
          humidity: [item.main.humidity],
          windSpeed: [item.wind.speed * 3.6],
          precipitation: [Math.round((item.pop || 0) * 100)],
          icons: [item.weather[0].icon]
        };
      } else {
        dailyData[dateKey].temps.push(item.main.temp);
        dailyData[dateKey].conditions.push(item.weather[0].main);
        dailyData[dateKey].descriptions.push(item.weather[0].description);
        dailyData[dateKey].humidity.push(item.main.humidity);
        dailyData[dateKey].windSpeed.push(item.wind.speed * 3.6);
        dailyData[dateKey].precipitation.push(Math.round((item.pop || 0) * 100));
        dailyData[dateKey].icons.push(item.weather[0].icon);
      }
    });
    
    // Convert to daily forecast format
    return Object.values(dailyData).slice(0, 7).map(day => {
      // Get most common condition for the day
      const conditionCounts = {};
      day.conditions.forEach(condition => {
        conditionCounts[condition] = (conditionCounts[condition] || 0) + 1;
      });
      const mostCommonCondition = Object.keys(conditionCounts).reduce((a, b) => 
        conditionCounts[a] > conditionCounts[b] ? a : b
      );
      
      return {
        date: day.date,
        high: Math.round(Math.max(...day.temps)),
        low: Math.round(Math.min(...day.temps)),
        condition: mostCommonCondition,
        description: day.descriptions[0], // Use first description
        humidity: Math.round(day.humidity.reduce((a, b) => a + b, 0) / day.humidity.length),
        windSpeed: Math.round(day.windSpeed.reduce((a, b) => a + b, 0) / day.windSpeed.length),
        precipitation: Math.max(...day.precipitation),
        icon: day.icons[0] // Use first icon
      };
    });
  };

  // Get location name from coordinates
  const getLocationName = async (lat, lon) => {
    try {
      const response = await fetch(
        `https://api.openweathermap.org/geo/1.0/reverse?lat=${lat}&lon=${lon}&limit=1&appid=d9427fe6ceeea54a3986bd4baf26350a`
      );
      const data = await response.json();
      
      if (data && data.length > 0) {
        const location = `${data[0].name}, ${data[0].state || data[0].country}`;
        return location;
      } else {
        return 'Unknown Location';
      }
    } catch (error) {
      console.error('Error getting location name:', error);
      return 'Current Location';
    }
  };

  // Generate mock weather data as fallback
  const generateMockWeatherData = () => {
    const conditions = ['Clear', 'Clouds', 'Rain', 'Thunderstorm', 'Drizzle', 'Mist'];
    const currentCondition = conditions[Math.floor(Math.random() * conditions.length)];
    
    return {
      location: 'Lagos, Nigeria',
      current: {
        temperature: Math.floor(Math.random() * 15) + 20,
        feelsLike: Math.floor(Math.random() * 15) + 18,
        condition: currentCondition,
        description: currentCondition.toLowerCase(),
        humidity: Math.floor(Math.random() * 30) + 50,
        windSpeed: Math.floor(Math.random() * 15) + 5,
        windDirection: Math.floor(Math.random() * 360),
        pressure: Math.floor(Math.random() * 50) + 1000,
        visibility: Math.floor(Math.random() * 5) + 8,
        uvIndex: Math.floor(Math.random() * 8) + 1,
        sunrise: new Date(),
        sunset: new Date(),
        icon: '01d'
      },
      forecast: Array.from({ length: 7 }, (_, i) => ({
        date: new Date(Date.now() + i * 24 * 60 * 60 * 1000),
        high: Math.floor(Math.random() * 10) + 25,
        low: Math.floor(Math.random() * 10) + 18,
        condition: conditions[Math.floor(Math.random() * conditions.length)],
        description: 'partly cloudy',
        humidity: Math.floor(Math.random() * 30) + 50,
        windSpeed: Math.floor(Math.random() * 15) + 5,
        precipitation: Math.floor(Math.random() * 50),
        icon: '01d'
      })),
      alerts: []
    };
  };

  // Search for farming information using web search
  const searchFarmingInfo = async (cropName) => {
    try {
      // Using a free AI model approach - we'll use a comprehensive database
      const farmingDatabase = {
        'Yam': {
          optimalTemp: { min: 25, max: 35 },
          optimalHumidity: { min: 60, max: 80 },
          optimalRainfall: { min: 100, max: 200 },
          conditions: ['Clear', 'Clouds'],
          advice: {
            good: "Excellent conditions for yam cultivation. Warm temperatures and adequate moisture are perfect for tuber development.",
            moderate: "Good conditions for yam growth. Monitor soil moisture and ensure proper drainage.",
            poor: "Challenging conditions for yam. Consider using mulch to retain moisture and protect from temperature extremes."
          }
        },
        'Plantain': {
          optimalTemp: { min: 26, max: 30 },
          optimalHumidity: { min: 70, max: 90 },
          optimalRainfall: { min: 150, max: 300 },
          conditions: ['Clear', 'Clouds', 'Rain'],
          advice: {
            good: "Perfect conditions for plantain cultivation. High humidity and warm temperatures are ideal for growth.",
            moderate: "Suitable conditions for plantain. Ensure consistent moisture and protect from strong winds.",
            poor: "Difficult conditions for plantain. Consider irrigation and wind protection measures."
          }
        },
        'Egg': {
          optimalTemp: { min: 18, max: 25 },
          optimalHumidity: { min: 50, max: 70 },
          optimalRainfall: { min: 0, max: 50 },
          conditions: ['Clear', 'Clouds'],
          advice: {
            good: "Ideal conditions for poultry farming. Moderate temperatures and low humidity are perfect for egg production.",
            moderate: "Good conditions for poultry. Ensure proper ventilation and temperature control.",
            poor: "Challenging conditions for poultry. Consider climate-controlled housing and ventilation systems."
          }
        },
        'Pepper': {
          optimalTemp: { min: 20, max: 30 },
          optimalHumidity: { min: 60, max: 80 },
          optimalRainfall: { min: 50, max: 150 },
          conditions: ['Clear', 'Clouds'],
          advice: {
            good: "Excellent conditions for pepper cultivation. Warm temperatures and moderate humidity promote healthy growth.",
            moderate: "Good conditions for pepper farming. Monitor for pests and ensure proper soil moisture.",
            poor: "Challenging conditions for pepper. Consider greenhouse cultivation or protective measures."
          }
        },
        'Rice': {
          optimalTemp: { min: 20, max: 35 },
          optimalHumidity: { min: 70, max: 90 },
          optimalRainfall: { min: 100, max: 200 },
          conditions: ['Clear', 'Clouds'],
          advice: {
            good: "Perfect conditions for rice cultivation. High humidity and warm temperatures are ideal.",
            moderate: "Adequate conditions for rice. Monitor water levels and soil moisture carefully.",
            poor: "Challenging conditions for rice. Consider irrigation systems and water management."
          }
        },
        'Maize': {
          optimalTemp: { min: 18, max: 30 },
          optimalHumidity: { min: 50, max: 80 },
          optimalRainfall: { min: 50, max: 150 },
          conditions: ['Clear', 'Clouds'],
          advice: {
            good: "Excellent conditions for maize growth. Optimal temperature range and adequate moisture.",
            moderate: "Good conditions for maize. Ensure proper soil moisture and drainage.",
            poor: "Suboptimal conditions for maize. Consider irrigation and soil management."
          }
        },
        'Tomatoes': {
          optimalTemp: { min: 20, max: 28 },
          optimalHumidity: { min: 60, max: 80 },
          optimalRainfall: { min: 30, max: 100 },
          conditions: ['Clear', 'Clouds'],
          advice: {
            good: "Ideal conditions for tomato cultivation. Perfect temperature and humidity for fruit development.",
            moderate: "Good conditions for tomatoes. Monitor for diseases and ensure proper support.",
            poor: "Challenging conditions for tomatoes. Consider greenhouse cultivation or delay planting."
          }
        },
        'Cassava': {
          optimalTemp: { min: 25, max: 35 },
          optimalHumidity: { min: 60, max: 85 },
          optimalRainfall: { min: 100, max: 300 },
          conditions: ['Clear', 'Clouds', 'Rain'],
          advice: {
            good: "Perfect conditions for cassava. High temperature and adequate moisture for root development.",
            moderate: "Suitable conditions for cassava. Monitor for drought stress and pests.",
            poor: "Challenging conditions for cassava. Consider irrigation and soil management."
          }
        }
      };

      return farmingDatabase[cropName] || farmingDatabase['Rice']; // Default fallback
    } catch (error) {
      console.error('Error searching farming info:', error);
      return null;
    }
  };

  // Generate farming insights based on weather and selected produce
  const generateFarmingInsight = async (produce, weather) => {
    if (!produce || !weather) return null;

    const cropData = await searchFarmingInfo(produce);
    if (!cropData) return null;
    
    // Analyze current conditions
    const temp = weather.current.temperature;
    const humidity = weather.current.humidity;
    const condition = weather.current.condition;
    const windSpeed = weather.current.windSpeed;
    const pressure = weather.current.pressure;
    
    let score = 0;
    let factors = [];

    // Temperature analysis
    if (temp >= cropData.optimalTemp.min && temp <= cropData.optimalTemp.max) {
      score += 3;
      factors.push(`✓ Optimal temperature (${temp}°C within ${cropData.optimalTemp.min}-${cropData.optimalTemp.max}°C range)`);
    } else if (temp >= cropData.optimalTemp.min - 5 && temp <= cropData.optimalTemp.max + 5) {
      score += 2;
      factors.push(`⚠ Moderate temperature (${temp}°C, slightly outside optimal range)`);
    } else {
      score += 1;
      factors.push(`✗ Temperature outside optimal range (${temp}°C vs ${cropData.optimalTemp.min}-${cropData.optimalTemp.max}°C)`);
    }

    // Humidity analysis
    if (humidity >= cropData.optimalHumidity.min && humidity <= cropData.optimalHumidity.max) {
      score += 2;
      factors.push(`✓ Good humidity levels (${humidity}% within ${cropData.optimalHumidity.min}-${cropData.optimalHumidity.max}% range)`);
    } else if (humidity >= cropData.optimalHumidity.min - 10 && humidity <= cropData.optimalHumidity.max + 10) {
      score += 1;
      factors.push(`⚠ Moderate humidity (${humidity}%, close to optimal range)`);
    } else {
      factors.push(`✗ Humidity not optimal (${humidity}% vs ${cropData.optimalHumidity.min}-${cropData.optimalHumidity.max}% range)`);
    }

    // Weather condition analysis
    if (cropData.conditions.includes(condition)) {
      score += 2;
      factors.push(`✓ Favorable weather conditions (${condition} is suitable for ${produce})`);
    } else {
      factors.push(`⚠ Weather conditions may affect growth (${condition} may not be ideal for ${produce})`);
    }

    // Additional factors
    if (windSpeed > 20) {
      factors.push(`⚠ High wind speed (${windSpeed} km/h) - may damage plants`);
    } else {
      factors.push(`✓ Moderate wind speed (${windSpeed} km/h) - good for air circulation`);
    }

    if (pressure < 1000) {
      factors.push(`⚠ Low atmospheric pressure (${pressure} hPa) - may indicate stormy weather`);
    } else {
      factors.push(`✓ Normal atmospheric pressure (${pressure} hPa)`);
    }

    // Determine overall assessment
    let assessment, advice, color;
    if (score >= 6) {
      assessment = "Excellent";
      advice = cropData.advice.good;
      color = "green";
    } else if (score >= 4) {
      assessment = "Good";
      advice = cropData.advice.moderate;
      color = "orange";
    } else {
      assessment = "Challenging";
      advice = cropData.advice.poor;
      color = "red";
    }

    return {
      assessment,
      advice,
      color,
      factors,
      score: `${score}/7`,
      recommendations: generateRecommendations(produce, weather, score)
    };
  };

  // Generate specific recommendations
  const generateRecommendations = (produce, weather, score) => {
    const recommendations = [];
    
    if (weather.current.temperature < 20) {
      recommendations.push("Consider using row covers or greenhouses for temperature protection");
    }
    
    if (weather.current.humidity < 50) {
      recommendations.push("Implement irrigation system to maintain soil moisture");
    }
    
    if (weather.current.humidity > 85) {
      recommendations.push("Ensure good drainage and air circulation to prevent fungal diseases");
    }
    
    if (weather.current.windSpeed > 20) {
      recommendations.push("Use windbreaks or stakes to protect young plants");
    }
    
    if (weather.forecast.some(day => day.precipitation > 70)) {
      recommendations.push("Monitor for potential flooding and ensure proper drainage");
    }
    
    if (score < 4) {
      recommendations.push("Consider delaying planting until conditions improve");
      recommendations.push("Use protective cultivation methods (greenhouse, tunnels)");
    }
    
    return recommendations;
  };

  // Handle produce selection change
  useEffect(() => {
    if (selectedProduce && weatherData) {
      generateFarmingInsight(selectedProduce, weatherData).then(insight => {
        setFarmingInsight(insight);
      });
    }
  }, [selectedProduce, weatherData]);

  const getWeatherIcon = (condition) => {
    switch(condition.toLowerCase()) {
      case 'clear':
        return <WiDaySunny size={40} color="#FFD700" />;
      case 'rain':
      case 'drizzle':
        return <WiRain size={40} color="#4682B4" />;
      case 'clouds':
      case 'partly cloudy':
        return <WiCloudy size={40} color="#A9A9A9" />;
      case 'thunderstorm':
        return <WiThunderstorm size={40} color="#4B0082" />;
      case 'mist':
      case 'fog':
        return <WiFog size={40} color="#D3D3D3" />;
      case 'snow':
        return <WiSnow size={40} color="#FFFFFF" />;
      default:
        return <WiDaySunny size={40} color="#FFD700" />;
    }
  };

  const formatTime = (date) => {
    return date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: true 
    });
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', { 
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="weather-widget">
      {loading ? (
        <div className="weather-loading">
          <div className="loading-spinner"></div>
          <p>Loading weather data...</p>
        </div>
      ) : weatherData ? (
        <>
          {/* Header */}
          <div className="weather-header">
            <div className="header-top">
              <h3>Weather Forecast</h3>
              <div className="header-controls">
                <div className="location-info">
                  <MapPin size={16} />
                  <span>{weatherData.location}</span>
                  {locationPermission && <span className="location-badge">GPS</span>}
                </div>
                <button 
                  onClick={refreshWeatherData}
                  className={`refresh-btn ${refreshing ? 'refreshing' : ''}`}
                  disabled={refreshing}
                  title="Refresh weather data"
                >
                  <RefreshCw size={16} />
                </button>
              </div>
            </div>
            {error && (
              <div className="weather-warning">
                <AlertTriangle size={14} />
                <span>{error}</span>
              </div>
            )}
          </div>
          
          {/* Current Weather */}
          <div className="current-weather">
            <div className="weather-main">
              <div className="weather-icon">
                {getWeatherIcon(weatherData.current.condition)}
              </div>
              <div className="weather-details">
                <div className="temperature">
                  <span className="temp-value">{weatherData.current.temperature}</span>
                  <span className="temp-unit">°C</span>
                </div>
                <p className="weather-condition">{weatherData.current.description}</p>
                <p className="feels-like">Feels like {weatherData.current.feelsLike}°C</p>
              </div>
            </div>

            {/* Separator */}
            <div className="weather-separator"></div>

            {/* Detailed Weather Stats - Desktop Grid */}
            <div className="weather-stats-grid">
              <div className="stat-item">
                <WiHumidity size={20} color="#4682B4" />
                <div>
                  <span className="stat-value">{weatherData.current.humidity}%</span>
                  <span className="stat-label">Humidity</span>
                </div>
              </div>
              <div className="stat-item">
                <WiStrongWind size={20} color="#87CEEB" />
                <div>
                  <span className="stat-value">{weatherData.current.windSpeed} km/h</span>
                  <span className="stat-label">Wind Speed</span>
                </div>
              </div>
              <div className="stat-item">
                <WiBarometer size={20} color="#8B4513" />
                <div>
                  <span className="stat-value">{weatherData.current.pressure} hPa</span>
                  <span className="stat-label">Pressure</span>
                </div>
              </div>
              <div className="stat-item">
                <Eye size={20} color="#708090" />
                <div>
                  <span className="stat-value">{weatherData.current.visibility} km</span>
                  <span className="stat-label">Visibility</span>
                </div>
              </div>
              <div className="stat-item">
                <WiThermometer size={20} color="#FF6347" />
                <div>
                  <span className="stat-value">{weatherData.current.uvIndex}</span>
                  <span className="stat-label">UV Index</span>
                </div>
              </div>
              <div className="stat-item">
                <Sun size={16} color="#FFD700" />
                <div>
                  <span className="stat-value">{formatTime(weatherData.current.sunrise)}</span>
                  <span className="stat-label">Sunrise</span>
                </div>
              </div>
            </div>

            {/* Mobile Weather Stats - Row Layout */}
            <div className="weather-stats-row">
              <div className="stat-row-item">
                <span className="stat-row-value">{weatherData.current.humidity}%</span>
                <span className="stat-row-label">Humidity</span>
              </div>
              <div className="stat-row-item">
                <span className="stat-row-value">{weatherData.current.windSpeed} km/h</span>
                <span className="stat-row-label">Wind Speed</span>
              </div>
              <div className="stat-row-item">
                <span className="stat-row-value">{weatherData.current.pressure} hPa</span>
                <span className="stat-row-label">Pressure</span>
              </div>
              <div className="stat-row-item">
                <span className="stat-row-value">{weatherData.current.visibility} km</span>
                <span className="stat-row-label">Visibility</span>
              </div>
              <div className="stat-row-item">
                <span className="stat-row-value">{weatherData.current.uvIndex}</span>
                <span className="stat-row-label">UV Index</span>
              </div>
              <div className="stat-row-item">
                <span className="stat-row-value">{formatTime(weatherData.current.sunrise)}</span>
                <span className="stat-row-label">Sunrise</span>
              </div>
            </div>
          </div>
          
          {/* Farming Insights Section */}
          <div className="farming-insights">
            <h4>Farming Insights</h4>
            <div className="produce-selector">
              <select 
                value={selectedProduce} 
                onChange={(e) => setSelectedProduce(e.target.value)}
                className="produce-select"
                disabled={loadingProduce}
              >
                <option value="">
                  {loadingProduce ? "Loading your produce..." : "Select a crop for analysis"}
                </option>
                {!loadingProduce && userProduce.length > 0 ? (
                  userProduce.map(produce => (
                    <option key={produce.id} value={produce.name}>
                      {produce.name}
                    </option>
                  ))
                ) : !loadingProduce && userProduce.length === 0 ? (
                  <option disabled>No produce found</option>
                ) : null}
              </select>
              {!loadingProduce && userProduce.length === 0 && (
                <p style={{fontSize: '12px', color: '#666', marginTop: '5px'}}>
                  No produce found. Add some crops to your farm to get farming insights.
                </p>
              )}
            </div>

            {farmingInsight && (
              <div className="insight-card">
                <div className="insight-header">
                  <h5>{selectedProduce} Farming Assessment</h5>
                  <div className={`assessment-badge ${farmingInsight.color}`}>
                    {farmingInsight.assessment}
                  </div>
                </div>
                <div className="insight-score">
                  <span>Score: {farmingInsight.score}</span>
                </div>
                <p className="insight-advice">{farmingInsight.advice}</p>
                
                <div className="insight-factors">
                  <h6>Analysis Factors:</h6>
                    <ul>
                    {farmingInsight.factors.map((factor, index) => (
                      <li key={index}>{factor}</li>
                    ))}
                  </ul>
                </div>

                {farmingInsight.recommendations.length > 0 && (
                  <div className="insight-recommendations">
                    <h6>Recommendations:</h6>
                    <ul>
                      {farmingInsight.recommendations.map((rec, index) => (
                        <li key={index}>{rec}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* 7-Day Forecast */}
          <div className="weather-forecast">
            <h4>7-Day Forecast</h4>
            <div className="forecast-grid">
            {weatherData.forecast.map((day, index) => (
              <div key={index} className="forecast-day">
                  <p className="forecast-dayname">{formatDate(day.date)}</p>
                {getWeatherIcon(day.condition)}
                <div className="forecast-temps">
                  <span className="forecast-high">{day.high}°</span>
                  <span className="forecast-low">{day.low}°</span>
                </div>
                  <div className="forecast-details">
                    <span className="forecast-humidity">{day.humidity}%</span>
                    <span className="forecast-precipitation">{day.precipitation}%</span>
                </div>
              </div>
            ))}
            </div>
          </div>

          {/* Weather Alerts */}
          {weatherData.alerts && weatherData.alerts.length > 0 && (
            <div className="weather-alerts">
              <h4>Weather Alerts</h4>
              {weatherData.alerts.map((alert, index) => (
                <div key={index} className="alert-item">
                  <AlertTriangle size={16} color="#FF6347" />
                  <div>
                    <strong>{alert.event}</strong>
                    <p>{alert.description}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      ) : (
        <div className="weather-error">
          <XCircle size={24} color="#FF6347" />
          <p>Weather data unavailable</p>
          <button onClick={getCurrentLocation} className="retry-button">
            Try Again
          </button>
        </div>
      )}

      <style>{`
        .weather-widget {
          background: white;
          border-radius: 12px;
          padding: 20px;
          box-shadow: 1px 2px 6px 1px rgba(0, 0, 0, 0.1);
          max-width: 100%;
        }

        .weather-loading {
          text-align: center;
          padding: 40px 20px;
        }

        .loading-spinner {
          width: 40px;
          height: 40px;
          border: 4px solid #f3f3f3;
          border-top: 4px solid #0e4410;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin: 0 auto 20px;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        .weather-header {
          margin-bottom: 20px;
        }

        .header-top {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 10px;
          color: black;
          gap: 20px;
              width: 100%;
        }

        .header-controls {
          display: flex;
          align-items: center;
          gap: 15px;
        }

        .location-info {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 14px;
          color: #666;
        }

        .location-badge {
          background: #0e4410;
          color: white;
          padding: 2px 6px;
          border-radius: 4px;
          font-size: 10px;
        }

        .refresh-btn {
          background: transparent;
          border: 1px solid #0e4410;
          color: #0e4410;
          padding: 8px;
          border-radius: 6px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s ease;
          min-width: 32px;
          height: 32px;
        }

        .refresh-btn:hover:not(:disabled) {
          background: #0e4410;
          color: white;
          transform: scale(1.05);
        }

        .refresh-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .refresh-btn.refreshing svg {
          animation: spin 1s linear infinite;
        }

        .weather-warning {
          display: flex;
          align-items: center;
          gap: 8px;
          background: #fff3cd;
          color: #856404;
          padding: 8px 12px;
          border-radius: 6px;
          font-size: 14px;
        }

        .current-weather {
          margin-bottom: 25px;
        }

        .weather-separator {
          height: 1px;
          background: #e0e0e0;
          margin: 15px 0;
        }

        .weather-main {
          display: flex;
          align-items: center;
          gap: 20px;
          margin-bottom: 20px;
          padding: 20px;
          background: #f8f9fa;
          border-radius: 12px;
        }

        .weather-icon {
          font-size: 48px;
        }

        .weather-icon-temp {
          display: flex;
          align-items: center;
          gap: 15px;
        }

        .temperature {
          display: flex;
          align-items: baseline;
          gap: 5px;
        }

        .weather-temp {
          display: flex;
          align-items: baseline;
        }

        .temp-value {
          font-size: 48px;
          font-weight: bold;
          color: #0e4410;
        }

        .temp-unit {
          font-size: 24px;
          color: #666;
          margin-left: 5px;
        }

        .weather-details {
          text-align: left;
          color: #333;
        }

        .weather-condition {
          font-size: 18px;
          font-weight: 500;
          margin: 0 0 5px 0;
          text-transform: capitalize;
        }

        .feels-like {
          font-size: 14px;
          color: #666;
          margin: 0;
        }

        .weather-stats-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 15px;
        }

        .weather-stats-row {
          display: none;
        }

        .stat-item {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 12px;
          background: #f8f9fa;
          border-radius: 8px;
        }

        .stat-value {
          font-weight: bold;
          color: #0e4410;
          display: block;
        }

        .stat-label {
          font-size: 12px;
          color: #666;
        }

        .stat-row-item {
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: 8px;
          background: #f8f9fa;
          border-radius: 6px;
          min-width: 60px;
          flex-shrink: 0;
        }

        .stat-row-value {
          font-weight: bold;
          color: #0e4410;
          font-size: 14px;
          margin-bottom: 2px;
        }

        .stat-row-label {
          font-size: 10px;
          color: #666;
          text-align: center;
        }

        .farming-insights {
          margin-bottom: 25px;
          padding: 20px;
          background: #f8f9fa;
          border-radius: 8px;
          color: #333;
        }

        .farming-insights * {
          color: inherit;
        }

        .farming-insights h4, 
        .farming-insights h5, 
        .farming-insights h6 {
          color: #0e4410 !important;
        }

        .farming-insights h4 {
          margin: 0 0 15px 0;
        }

        .produce-selector {
          margin-bottom: 20px;
        }

        .produce-select {
          width: 100%;
          padding: 10px;
          border: 1px solid #ddd;
          border-radius: 6px;
          font-size: 14px;
          color: #333;
          background: white;
        }

        .insight-card {
          background: white;
          padding: 15px;
          border-radius: 8px;
          border-left: 4px solid #0e4410;
          color: #333;
        }

        .insight-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 10px;
        }

        .insight-header h5 {
          margin: 0;
          color: #0e4410;
        }

        .assessment-badge {
          padding: 4px 12px;
          border-radius: 20px;
          font-size: 12px;
          font-weight: bold;
          text-transform: uppercase;
        }

        .assessment-badge.green {
          background: #d4edda;
          color: #155724;
        }

        .assessment-badge.orange {
          background: #fff3cd;
          color: #856404;
        }

        .assessment-badge.red {
          background: #f8d7da;
          color: #721c24;
        }

        .insight-score {
          margin-bottom: 10px;
          font-weight: bold;
          color: #0e4410;
        }

        .insight-advice {
          margin-bottom: 15px;
          line-height: 1.5;
          color: #333;
        }

        .insight-factors, .insight-recommendations {
          margin-bottom: 15px;
        }

        .insight-factors h6, .insight-recommendations h6 {
          margin: 0 0 8px 0;
          color: #0e4410;
          font-size: 14px;
        }

        .insight-factors ul, .insight-recommendations ul {
          margin: 0;
          padding-left: 20px;
        }

        .insight-factors li, .insight-recommendations li {
          margin-bottom: 5px;
          font-size: 14px;
          line-height: 1.4;
          color: #333;
        }

        .weather-forecast h4 {
          margin: 0 0 15px 0;
          color: #0e4410;
        }

        .forecast-grid {
          display: flex;
          flex-direction: row;
          gap: 10px;
          overflow-x: auto;
          padding-bottom: 10px;
          scrollbar-width: thin;
          scrollbar-color: #0e4410 #f1f1f1;
        }

        .forecast-grid::-webkit-scrollbar {
          height: 6px;
        }

        .forecast-grid::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 3px;
        }

        .forecast-grid::-webkit-scrollbar-thumb {
          background: #0e4410;
          border-radius: 3px;
        }

        .forecast-grid::-webkit-scrollbar-thumb:hover {
          background: #0a2e08;
        }

        .forecast-day {
          text-align: center;
          padding: 15px 10px;
          background: #f8f9fa;
          border-radius: 8px;
          min-width: 100px;
          flex-shrink: 0;
        }

        .forecast-dayname {
          font-size: 12px;
          font-weight: bold;
          margin: 0 0 10px 0;
          color: #0e4410;
        }

        .forecast-temps {
          margin: 10px 0;
        }

        .forecast-high {
          font-weight: bold;
          color: #0e4410;
          margin-right: 5px;
        }

        .forecast-low {
          color: #666;
        }

        .forecast-details {
          font-size: 11px;
          color: #666;
        }

        .forecast-humidity, .forecast-precipitation {
          display: block;
          margin: 2px 0;
        }

        .weather-alerts {
          margin-top: 20px;
        }

        .weather-alerts h4 {
          margin: 0 0 15px 0;
          color: #FF6347;
        }

        .alert-item {
          display: flex;
          align-items: flex-start;
          gap: 10px;
          padding: 12px;
          background: #fff5f5;
          border-radius: 8px;
          margin-bottom: 10px;
        }

        .weather-error {
          text-align: center;
          padding: 40px 20px;
          color: #FF6347;
        }

        .retry-button {
          background: #0e4410;
          color: white;
          border: none;
          padding: 10px 20px;
          border-radius: 6px;
          cursor: pointer;
          margin-top: 15px;
        }

        .retry-button:hover {
          background: #0a2e08;
        }

        @media (max-width: 768px) {
          .weather-widget {
            padding: 15px;
          }

          .header-top {
            flex-direction: column;
            align-items: flex-start;
            gap: 10px;
          }

          .header-controls {
            width: 100%;
            justify-content: space-between;
          }
          
          /* Hide desktop grid, show mobile row */
          .weather-stats-grid {
            display: none;
          }
          
          .weather-stats-row {
            display: flex;
            flex-direction: row;
            gap: 8px;
            overflow-x: auto;
            padding-bottom: 8px;
            scrollbar-width: thin;
            scrollbar-color: #0e4410 #f1f1f1;
          }
          
          .weather-stats-row::-webkit-scrollbar {
            height: 4px;
          }
          
          .weather-stats-row::-webkit-scrollbar-track {
            background: #f1f1f1;
            border-radius: 2px;
          }
          
          .weather-stats-row::-webkit-scrollbar-thumb {
            background: #0e4410;
            border-radius: 2px;
          }
          
          .forecast-grid {
            gap: 8px;
          }
          
          .forecast-day {
            min-width: 80px;
            padding: 10px 8px;
          }
          
          .weather-main {
            text-align: center;
            gap: 15px;
            padding: 15px;
          }
          
          .weather-details {
            text-align: center;
            color: #333;
          }
          
          .temp-value {
            font-size: 36px;
          }
          
          .temp-unit {
            font-size: 18px;
          }
          
          .farming-insights {
            padding: 15px;
          }
          
          .insight-card {
            padding: 12px;
          }

          .refresh-btn {
            min-width: 28px;
            height: 28px;
            padding: 6px;
          }
        }

        @media (max-width: 480px) {
          .weather-stats-grid {
            display: none;
          }
          
          .weather-stats-row {
            display: flex;
            gap: 6px;
                    width: 100%;
          }
          
          .stat-row-item {
            min-width: 50px;
            padding: 6px;
          }
          
          .stat-row-value {
            font-size: 12px;
          }
          
          .stat-row-label {
            font-size: 9px;
          }
          
          .forecast-day {
            min-width: 70px;
            padding: 8px 6px;
          }
          
          .forecast-dayname {
            font-size: 10px;
          }
          
          .forecast-high, .forecast-low {
            font-size: 12px;
          }
        }
      `}</style>
    </div>
  );
};

export default WeatherWidget;